import {useRef} from "react";
import {inBrowser, noop} from "@firefly/fly-defter";
import useEffectWithTarget from "./useEffectWithTarget";
import {BasicTarget, getTargetElement} from "./target";
import useMemoizedFn from "../effect/useMemoizedFn";

// type MouseOrTouchEvent = MouseEvent | TouchEvent;
export interface Options {
    delay?: number;
    moveThreshold?: { x?: number; y?: number };
    onClick?: (event: Event) => void;
    onLongPress?: (event: Event) => void;
    onLongPressEnd?: (event: Event) => void;
}

const touchSupported =
    inBrowser &&
    // @ts-ignore
    ('ontouchstart' in window || (window.DocumentTouch && document instanceof DocumentTouch));

function useLongPress(
    target: BasicTarget,
    options: Options = {

    },
) {
    const {
        delay = 1000,
        moveThreshold = {
            x: 8,
            y: 8,
        },
        onClick: _onClick = noop,
        onLongPress: _onLongPress = noop,
        onLongPressEnd: _onLongPressEnd = noop
    } = options;

    const onClick = useMemoizedFn(_onClick);
    const onLongPress = useMemoizedFn(_onLongPress);
    const onLongPressEnd = useMemoizedFn(_onLongPressEnd);
    const longPressed = useRef<boolean>(false);

    const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | number>();
    const triggering = useRef(false);
    const pervPositionRef = useRef({x: 0, y: 0});
    const hasMoveThreshold = !!(
        (moveThreshold?.x && moveThreshold.x > 0) ||
        (moveThreshold?.y && moveThreshold.y > 0)
    );

    useEffectWithTarget(
        () => {
            const targetElement = getTargetElement(target);
            if (!targetElement?.addEventListener) {
                return;
            }

            const overThreshold = (event: Event) => {
                const {clientX, clientY} = getClientPosition(event);
                const offsetX = Math.abs(clientX - pervPositionRef.current.x);
                const offsetY = Math.abs(clientY - pervPositionRef.current.y);

                return !!(
                    (moveThreshold?.x && offsetX > moveThreshold.x) ||
                    (moveThreshold?.y && offsetY > moveThreshold.y)
                );
            };

            function getClientPosition(event: Event) {
                if (event instanceof TouchEvent) {
                    return {
                        clientX: event.touches[0]!.clientX,
                        clientY: event.touches[0]!.clientY,
                    };
                }

                if (event instanceof MouseEvent) {
                    return {
                        clientX: event.clientX,
                        clientY: event.clientY,
                    };
                }
                return {clientX: 0, clientY: 0};
            }

            function triggerLongPress(event: Event) {
                if (triggering.current) {
                    onLongPress(event);
                    longPressed.current = true;
                    triggering.current = false;
                }
            }
            function reset() {
                triggering.current = false;
                longPressed.current = false;
                if (longPressTimerRef.current) {
                    clearTimeout(longPressTimerRef.current);
                }
            }
            function cancel() {
                reset();
            }
            const onStart = (event: Event) => {
                if (hasMoveThreshold) {
                    const {clientX, clientY} = getClientPosition(event);
                    pervPositionRef.current.x = clientX;
                    pervPositionRef.current.y = clientY;
                }
                triggering.current = true;
                longPressed.current = false;
                longPressTimerRef.current = setTimeout(triggerLongPress, delay);
            };

            const onMove = (event: Event) => {
                if (triggering.current) {
                    if (hasMoveThreshold) {
                        if (overThreshold(event)) {
                            cancel();
                        }
                    }
                }
            };

            const onEnd = (event: Event, shouldTriggerClick: boolean = false) => {
                if (longPressed.current) {
                    onLongPressEnd(event);
                } else if (triggering.current) {
                    if (shouldTriggerClick) {
                        onClick(event);
                    }
                }
                reset();
            };

            const onEndWithClick = (event: Event) => onEnd(event, true);

            if (!touchSupported) {
                targetElement.addEventListener('mousedown', onStart);
                targetElement.addEventListener('mouseup', onEndWithClick);
                targetElement.addEventListener('mouseleave', onEnd);
                if (hasMoveThreshold) targetElement.addEventListener('mousemove', onMove);
            } else {
                targetElement.addEventListener('touchstart', onStart);
                targetElement.addEventListener('touchend', onEndWithClick);
                if (hasMoveThreshold) targetElement.addEventListener('touchmove', onMove);
            }
            return () => {
                cancel();
                if (!touchSupported) {
                    targetElement.removeEventListener('mousedown', onStart);
                    targetElement.removeEventListener('mouseup', onEndWithClick);
                    targetElement.removeEventListener('mouseleave', onEnd);
                    if (hasMoveThreshold) targetElement.removeEventListener('mousemove', onMove);
                } else {
                    targetElement.removeEventListener('touchstart', onStart);
                    targetElement.removeEventListener('touchend', onEndWithClick);
                    if (hasMoveThreshold) targetElement.removeEventListener('touchmove', onMove);
                }
            };
        },
        [],
        target,
    );
}

export default useLongPress;
